import { createFileRoute, useNavigate } from '@tanstack/react-router';
import React from 'react';

import { handleUnexpectedError } from '@/app/errors';
import { useCurrentUser } from '@/app/hooks/use-current-user';
import { queryClient } from '@/app/lib/tanstack-query';
import { Button, ButtonGroup } from '@/common/components/button';
import { Field, FieldInfo, Label } from '@/common/components/fieldset';
import { Heading } from '@/common/components/heading';
import { Text } from '@/common/components/text';
import { userKeys } from '@/shared/user/queries';
import { useLinkMembershipForm } from '@/wizard/forms/link-membership-form';
import { useLinkMembership } from '@/wizard/queries';

export const Route = createFileRoute('/wizard/memberships/new')({
  component: LinkMembershipPage,
  head: () => {
    return {
      meta: [
        { title: 'Link Membership | myTalquin Member Portal' }
      ]
    };
  }
});

function LinkMembershipPage() {
  const { id: userId } = useCurrentUser();
  const navigate = useNavigate();
  const linkMembershipMutation = useLinkMembership();

  const form = useLinkMembershipForm({
    defaultValues: {
      memberNumber: '',
      memberPin: ''
    },
    onSubmit: async (values) => {
      const { memberNumber, memberPin } = values;
      await linkMembershipMutation.mutateAsync(
        {
          userId,
          memberNumber: parseInt(memberNumber),
          memberPin
        },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({
              queryKey: userKeys.authenticated()
            });

            await navigate({
              to: '/wizard/memberships/$member-number/aliases',
              params: { 'member-number': memberNumber }
            });
            form.reset();
          },
          onError: error => handleUnexpectedError(error)
        }
      );
    }
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    await form.handleSubmit(event);
  };

  return (
    <>
      <Heading>Link Membership</Heading>
      <Text>Your Member number and Member PIN are located on your Talquin Electric bill. Please enter them below.</Text>

      <form onSubmit={handleSubmit} className="grid w-full grid-cols-1 gap-8">
        <form.AppField name="memberNumber">
          {field => (
            <Field>
              <Label>Member Number</Label>
              <field.FormInput type="text" maxLength={19} />
              <FieldInfo field={field} />
            </Field>
          )}
        </form.AppField>
        <form.AppField name="memberPin">
          {field => (
            <Field>
              <Label>Member PIN</Label>
              <field.FormInput type="text" maxLength={6} />
              <FieldInfo field={field} />
            </Field>
          )}
        </form.AppField>
        <form.AppForm>
          <ButtonGroup>
            <Button plain to="/settings/memberships">Cancel</Button>
            <Button type="submit">Link Membership</Button>
          </ButtonGroup>
        </form.AppForm>
      </form>
    </>
  );
}
